// 2026-07-09 - FormuLearning - Autosave 지연저장 공통 훅 (이슈 #33 4단계)
// 2026-11-03 수정 - 이슈 #114: viewer 는 저장·버퍼 없이 화면 반영만(`isReadOnly`).
// 파일 위치: frontend/src/hooks/useDeferredSave.ts
// StepCard의 필드 편집 저장을 Autosave 모드에 따라 처리한다.
//   - ON : 편집 즉시 PUT → 응답으로 setData (현행과 동일)
//   - OFF: PUT 보류. 로컬 낙관적 setData + patch 누적(dirty). Save & Continue 시 flush로 일괄 PUT.
// flush 실행기는 전역 saveBufferStore에 등록되어 MainPage.handleSaveAndContinue가 호출한다.
// 2026-09-23 수정 - 이슈 #82: 진행 중 작업(`busyStore`)이 있으면 ON 이어도 **OFF 처럼 버퍼**하고(Autosave 보류),
//   작업이 끝나면 ON 일 때 남은 버퍼를 flush 한다. 보류는 미루는 것이지 버리지 않는다 — 진행 중 버퍼는 미저장 배지로 보인다.
//   카드 잠금으로 새 편집은 생기지 않으므로 방어 경로다(잠금 직전 입력의 늦은 저장).
// 2026-09-24 수정 - 이슈 #75(M915-04): 선택 옵션 `beforeSave(explicit)` — 서버 저장 직전 확인(Step 3 주성분 확정,
//   BR-STEP-03). ON 즉시 저장에서 거절되면 **OFF 처럼 버퍼**하고(편집 유지·미저장 배지), flush(명시 저장)에서
//   거절되면 저장하지 않고 예외로 끝낸다 — Save and continue 가 다음 스텝으로 넘어가지 않는다.
//   화면이 스스로 넣는 기본값(Y 기본 행 시딩 등)은 `apply(patch, { system: true })` 로 이 확인을 건너뛴다 —
//   사용자 저장이 아니다(확인하지 않으면 프로젝트를 열기만 해도 확인창이 뜬다).

import { useCallback, useEffect, useRef } from 'react'
import { useUiStore } from '../stores/uiStore'
import { useSaveBuffer } from '../stores/saveBufferStore'
import { useBusyStore } from '../stores/busyStore'
import { isReadOnly } from '../lib/readOnly'

interface DeferredSaveOptions<TData> {
  step: number
  projectId: string | undefined
  data: TData | null
  setData: (d: TData) => void
  save: (projectId: string, patch: Partial<TData>) => Promise<TData>
  normalize?: (d: TData) => TData
  beforeSave?: (explicit: boolean) => Promise<boolean>
}

interface DeferredSaveResult<TData> {
  apply: (patch: Partial<TData>, opts?: { system?: boolean }) => Promise<void> // 필드 편집 저장(ON=즉시, OFF=버퍼)
  dirty: boolean // 이 스텝에 미저장 편집이 있는지
}

export function useDeferredSave<TData extends object>(
  opts: DeferredSaveOptions<TData>,
): DeferredSaveResult<TData> {
  const { step, projectId, data, setData, save, normalize, beforeSave } = opts
  const autosave = useUiStore((s) => s.autosave)
  const markDirty = useSaveBuffer((s) => s.markDirty)
  const registerFlusher = useSaveBuffer((s) => s.registerFlusher)
  const unregister = useSaveBuffer((s) => s.unregister)
  const dirty = useSaveBuffer((s) => s.dirtySteps[step] ?? false)
  const busy = useBusyStore((s) => s.task !== null)

  // 최신 data·정규화·저장 함수를 ref로 추적(낙관적 병합·flush에서 stale 클로저 방지).
  const dataRef = useRef(data)
  const pendingRef = useRef<Partial<TData> | null>(null)
  const saveRef = useRef(save)
  const normalizeRef = useRef(normalize)
  const setDataRef = useRef(setData)
  const projectIdRef = useRef(projectId)
  const beforeSaveRef = useRef(beforeSave)
  useEffect(() => {
    beforeSaveRef.current = beforeSave
    dataRef.current = data
    saveRef.current = save
    normalizeRef.current = normalize
    setDataRef.current = setData
    projectIdRef.current = projectId
  })

  const norm = useCallback((d: TData): TData => (normalizeRef.current ? normalizeRef.current(d) : d), [])

  const apply = useCallback(
    async (patch: Partial<TData>, opts?: { system?: boolean }) => {
      const pid = projectIdRef.current
      if (!pid) return
      // viewer(#114) — 저장·버퍼 없이 화면에만 반영한다(시딩 등 화면이 스스로 넣는 값). 편집 셀은 비활성이라 방어 경로다.
      if (isReadOnly()) {
        if (dataRef.current != null) {
          const merged = norm({ ...dataRef.current, ...patch } as TData)
          dataRef.current = merged
          setDataRef.current(merged)
        }
        return
      }
      if (useUiStore.getState().autosave && !useBusyStore.getState().task
          && (opts?.system || !beforeSaveRef.current || await beforeSaveRef.current(false))) {
        // ON — 즉시 PUT
        const updated = await saveRef.current(pid, patch)
        setDataRef.current(norm(updated))
        return
      }
      // OFF — 낙관적 로컬 반영 + 누적, PUT 보류
      pendingRef.current = { ...(pendingRef.current ?? {}), ...patch }
      if (dataRef.current != null) {
        const merged = norm({ ...dataRef.current, ...patch } as TData)
        dataRef.current = merged
        setDataRef.current(merged)
      }
      markDirty(step, true)
    },
    [step, markDirty, norm],
  )

  // flush 실행기 등록 — 누적 patch를 일괄 PUT하고 dirty 해제.
  useEffect(() => {
    registerFlusher(step, async () => {
      const pid = projectIdRef.current
      if (!pid) return
      // 명시 저장은 미저장분이 없어도 확인한다 — Step 3 Save and continue 가 곧 «저장» 이다(BR-STEP-03).
      if (beforeSaveRef.current && !(await beforeSaveRef.current(true))) throw new Error('save declined')
      const patch = pendingRef.current
      if (!patch) return
      const updated = await saveRef.current(pid, patch)
      pendingRef.current = null
      const normed = norm(updated)
      dataRef.current = normed
      setDataRef.current(normed)
    })
    return () => unregister(step)
  }, [step, registerFlusher, unregister, norm])

  // Autosave를 다시 ON으로 바꾸거나 진행 중 작업이 끝나면(#82) 남은 미저장분을 잃지 않도록 즉시 flush.
  useEffect(() => {
    if (autosave && !busy && pendingRef.current) {
      useSaveBuffer.getState().flushStep(step).catch(() => { /* 저장 직전 확인 거절(#75) — 버퍼는 그대로 남는다 */ })
    }
  }, [autosave, busy, step])

  return { apply, dirty }
}
